import React, { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@roger/ui-components';
import { ArrowLeft, X } from 'lucide-react';
import { useStore } from '../../store';
import {
  chatKeys,
  flattenMessages,
  lastFetchedSeq,
  useChannel,
  useMarkRead,
  useMessages,
  usePendingSends,
  useSendMessage,
} from '../../hooks/chat/useChatQueries';
import { useAttachmentUpload } from '../../hooks/chat/useAttachmentUpload';
import { useAttachmentUrls } from '../../hooks/chat/useAttachmentUrls';
import { useEventsConnectionState } from '../../hooks/chat/useChatEvents';
import type { AttachmentOut } from '../../services/api';
import { acceptFiles, FOLDER_NOTICE } from '../../utils/attachments';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { AttachButton } from './AttachButton';
import { AttachmentLightbox } from './AttachmentLightbox';
import { MessageList } from './MessageList';
import { ChannelMenu } from './ChannelMenu';
import { ConnectionStrip } from './ConnectionStrip';
import { PendingAttachmentChip } from './PendingAttachmentChip';
import { SEND_INPUT_ID, SendBar } from './SendBar';

export function ChatChannel() {
  const channelId = useStore(state => state.activeChannelId) ?? '';
  const setChatView = useStore(state => state.setChatView);
  const closeChat = useStore(state => state.closeChat);
  const draft = useStore(state => state.chatDrafts[channelId] ?? '');
  const setChatDraft = useStore(state => state.setChatDraft);
  const pending = usePendingSends(channelId);
  const currentUserId = useStore(state => state.user?.id ?? '');
  const showError = useStore(state => state.showError);

  const { data: header, error: headerError } = useChannel(channelId);
  const messagesQuery = useMessages(channelId);
  const messages = flattenMessages(messagesQuery.data?.pages);
  // One call for the whole page: one batch request and one audit row per file.
  const { urlsFor, download } = useAttachmentUrls();
  const pageAttachments = messages.flatMap(m => m.attachments ?? []);
  const urls = urlsFor(
    pageAttachments.map(a => a.id),
    pageAttachments.filter(a => a.status === 'ready').map(a => a.id)
  );
  const [lightbox, setLightbox] = useState<{
    tiles: AttachmentOut[];
    index: number;
  } | null>(null);
  const { send } = useSendMessage();
  const markRead = useMarkRead();
  const queryClient = useQueryClient();
  const [archivedOnSend, setArchivedOnSend] = useState(false);
  // The one notice line in the send bar: picker, drop, paste and the upload
  // URL's 422 all write here, and it clears after 3.5 s (spec, Attach).
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(null), 3500);
    return () => clearTimeout(timer);
  }, [notice]);
  const { chips, addFiles, remove, retry, clearSubmitted, uploading, doneIds } =
    useAttachmentUpload(channelId, setNotice);
  // 'stopped' is the state ConnectionStrip calls offline.
  const offline = useEventsConnectionState() === 'stopped';

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    // No send bar, no attach: a chip would be invisible and its upload an
    // orphan. Offline disables every route in, as it disables the plus.
    if (!canWrite || offline) return;
    const items = Array.from(event.dataTransfer.items ?? []);
    if (items.some(item => item.webkitGetAsEntry?.()?.isDirectory)) {
      setNotice(FOLDER_NOTICE);
      return;
    }
    addFiles(
      acceptFiles(
        Array.from(event.dataTransfer.files ?? []),
        chips.length,
        setNotice
      )
    );
  };

  // A paste in the textarea bubbles here, so SendBar needs no handler.
  const handlePaste = (event: React.ClipboardEvent) => {
    if (!canWrite || offline) return;
    const pasted = Array.from(event.clipboardData.files ?? []);
    if (pasted.length === 0) return;
    addFiles(acceptFiles(pasted, chips.length, setNotice));
  };

  // The channel was archived away, or membership was lost: go back to the inbox
  // rather than show an empty header over an empty list.
  const leaveGone = useCallback(() => {
    setChatView('inbox');
    showError('This conversation is no longer available.');
    void queryClient.invalidateQueries({ queryKey: chatKeys.inbox() });
  }, [setChatView, showError, queryClient]);

  useEffect(() => {
    if (
      getHttpStatus(headerError) === 404 ||
      getHttpStatus(messagesQuery.error) === 404
    ) {
      leaveGone();
    }
  }, [headerError, messagesQuery.error, leaveGone]);

  // Any other load failure: say so rather than claim the channel is empty or
  // that only announcers may post.
  const loadError = [headerError, messagesQuery.error].find(
    error => error && getHttpStatus(error) !== 404
  );
  useEffect(() => {
    if (!loadError) return;
    console.error('Failed to load conversation', loadError);
    showError(
      'Failed to load conversation',
      getBusinessErrorMessage(loadError) ?? undefined
    );
  }, [loadError, showError]);

  // Mark read when the channel opens and when history arrives. Keyed on the
  // seq, not the array: `flattenMessages` returns a new array each render. The
  // seq is the newest fetched one, so a row that only a send response cached
  // cannot mark an unfetched earlier message read.
  const readSeq = lastFetchedSeq(queryClient, channelId, messages);
  useEffect(() => {
    if (!channelId || readSeq === 0) return;
    if (document.visibilityState !== 'visible') return;
    markRead(channelId, readSeq);
  }, [channelId, readSeq, markRead]);

  const doSend = async (body: string, clientId?: string) => {
    // First attempt: the done chips. Retry: the ids that attempt recorded on
    // its mutation, never whatever the composer holds now.
    const attachmentIds = clientId
      ? (pending.find(p => p.client_id === clientId)?.attachment_ids ?? [])
      : doneIds;
    // Clear before the request, so a second Enter while it runs has nothing to
    // send. A transient failure stays visible as its pending row with Retry.
    if (!clientId) {
      setChatDraft(channelId, '');
      // A chip whose upload failed is not in `doneIds`, so the send silently
      // drops it. The chip stays in the bar with Try again, but say so: the
      // person has already pressed Enter and believes the file went.
      // Name the files: with several chips in the bar, "the file" names none.
      const failed = chips
        .filter(chip => chip.state === 'failed')
        .map(chip => chip.fileName);
      if (failed.length === 1) {
        setNotice(`Sent without ${failed[0]}. Try again or remove it.`);
      } else if (failed.length > 1) {
        setNotice(
          `Sent without ${failed.length} files: ${failed.join(', ')}. Try again or remove them.`
        );
      }
      clearSubmitted(attachmentIds);
    }
    try {
      await send(channelId, body, clientId, attachmentIds);
    } catch (error) {
      const status = getHttpStatus(error);
      if (status === 404) {
        leaveGone();
        return;
      }
      if (status === 409 || status === 403) {
        // `send` has dropped the failed row, kept the text as the draft and
        // refetched the header. 409: replace the bar now, before that refetch.
        // The chips are not restored: their rows stay pending and the hourly
        // sweep removes them.
        if (status === 409) setArchivedOnSend(true);
        return;
      }
      if (status === 422) {
        // A file the server would not link (expired, swept, never uploaded):
        // a Retry resends the same ids and fails again, so `send` dropped the
        // row and kept the text; the person attaches the files again.
        setNotice(
          getBusinessErrorMessage(error) ??
            'The files could not be sent. Attach them again.'
        );
        return;
      }
      showError(
        'Failed to send message',
        getBusinessErrorMessage(error) ?? undefined
      );
    }
  };

  const archived = archivedOnSend || header?.archived === true;
  // The same condition that renders SendBar below.
  const canWrite = !loadError && !archived && header?.can_write === true;
  const subtitle = header
    ? (header.subtitle ??
      (header.kind === 'announcement'
        ? 'announcers only'
        : `${header.member_count} members`))
    : '';

  return (
    <div
      data-testid='chat-panel'
      className='flex min-h-0 flex-1 flex-col'
      onDragOver={event => event.preventDefault()}
      onDrop={handleDrop}
      onPaste={handlePaste}
    >
      <div className='flex items-center gap-1 border-b px-2 py-2'>
        <Button
          size='icon'
          variant='ghost'
          aria-label='Back'
          onClick={() => setChatView('inbox')}
        >
          <ArrowLeft className='h-4 w-4' />
        </Button>
        <div className='min-w-0 flex-1'>
          <p className='truncate text-sm font-semibold'>
            {header?.title ?? ''}
          </p>
          <p className='truncate text-[11px] text-muted-foreground'>
            {subtitle}
          </p>
        </div>
        {header && (
          <ChannelMenu
            header={header}
            onShowMembers={() => setChatView('members')}
            onShowAnnouncers={() => setChatView('announcers')}
          />
        )}
        <Button
          size='icon'
          variant='ghost'
          aria-label='Close'
          onClick={closeChat}
        >
          <X className='h-4 w-4' />
        </Button>
      </div>
      <ConnectionStrip />

      {loadError ? (
        <p className='flex-1 px-6 py-8 text-center text-sm text-muted-foreground'>
          Messages are not available.
        </p>
      ) : !messagesQuery.isLoading &&
        messages.length === 0 &&
        pending.length === 0 ? (
        // Errors table: an empty channel gets short centered copy and one action.
        <div className='flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center'>
          <p className='text-sm text-muted-foreground'>No messages yet</p>
          {header?.can_write && !archived && (
            <Button
              size='sm'
              onClick={() => document.getElementById(SEND_INPUT_ID)?.focus()}
            >
              Write a message
            </Button>
          )}
        </div>
      ) : (
        <MessageList
          messages={messages}
          pending={pending}
          currentUserId={currentUserId}
          lastReadSeq={header?.last_read_seq ?? 0}
          hasOlder={!!messagesQuery.hasNextPage}
          isLoadingOlder={messagesQuery.isFetchingNextPage}
          onLoadOlder={() => void messagesQuery.fetchNextPage()}
          onRetry={(clientId, body) => void doSend(body, clientId)}
          urls={urls}
          onDownloadAttachment={id => void download(id)}
          onOpenAttachment={(tiles, index) => setLightbox({ tiles, index })}
        />
      )}

      {loadError ? null : archived ? (
        <p className='border-t px-3 py-3 text-center text-xs text-muted-foreground'>
          This conversation is archived.
        </p>
      ) : header?.can_write ? (
        <SendBar
          title={header.title}
          value={draft}
          onChange={value => setChatDraft(channelId, value)}
          onSend={() => void doSend(draft)}
          disabled={uploading}
          canSendWhenEmpty={doneIds.length > 0}
          aboveInput={
            chips.length > 0 || notice ? (
              <div className='flex flex-col gap-1 px-2 pt-2'>
                {chips.map(chip => (
                  <PendingAttachmentChip
                    key={chip.localId}
                    chip={chip}
                    onRemove={remove}
                    onRetry={retry}
                  />
                ))}
                {notice && <p className='text-xs text-destructive'>{notice}</p>}
              </div>
            ) : undefined
          }
          toolbarStart={
            <AttachButton
              existingCount={chips.length}
              disabled={offline}
              onFiles={addFiles}
              onNotice={setNotice}
            />
          }
        />
      ) : (
        <p className='border-t px-3 py-3 text-center text-xs text-muted-foreground'>
          Only announcers can post here.
        </p>
      )}
      {lightbox && (
        <AttachmentLightbox
          attachments={lightbox.tiles}
          index={lightbox.index}
          urls={urls}
          onIndexChange={index => setLightbox({ ...lightbox, index })}
          onClose={() => setLightbox(null)}
          onDownload={id => void download(id)}
        />
      )}
    </div>
  );
}
